import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import React, { memo, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import BookingPanel from '../../../components/Booking/BookingPanel/BookingPanel';
import ListingFunctionsSection from '../../../components/Booking/BookingPanel/ListingFunctionsSection';
import IconLocationPin from '../../../components/Icons/IconLocationPin';
import config from '../../../config';
import { petrol } from '../../../styles/mui/colors';
import useAppSelector from '../../../util/hooks/useAppSelector';
import usePass from '../../../util/hooks/usePass';
import { isCampingGroundListing } from '../../../util/data';
import { getDistanceToListing, hasLocationPermission, userLocation } from '../../../util/maps';
import { findOptionsForSelectFilter } from '../../../util/search';
import { REQUEST_BOOKING } from '../../../util/transaction.default';

const getCategoryLabel = (categories, key, intl) => {
  const cat = categories.find((c) => c.key === key);
  return cat ? intl.formatMessage({ id: cat.label }) : key;
};

const SectionHeadingComponent = ({
  // Listing data and status
  currentListing,
  richTitle,
  authorDisplayName = '',

  // Pricing and booking details
  formattedPrice,
  priceTitle,
  bookingLimit = null,
  minimumNights = 1,
  voucher = null,
  voucherValidationInProgress = false,
  voucherValidationError = null,

  // Internationalization and translation
  isTranslated = false,
  getTranslation,

  // User and booking information
  toManyTransactionsWithProvider = false,

  // Reviews and social interaction
  reviewsRef,
  onShareListing,

  // Booking and listing management
  onSubmit,
  onFetchTransactionLineItems,
  isOwnListing,

  // Timeslots and availability
  timeSlots,
  onFetchTimeSlots,
  fetchTimeSlotsInProgress = false,
  fetchTimeSlotsError = null,

  // Financial elements and transactions
  lineItems = [],
  fetchLineItemsInProgress = false,
  fetchLineItemsError = null,
  customLineItemTranslations = {},

  // Other UI elements and states
  openShareModal = false,
  title,

  scrollTargetRef,
  showReducedPrice = false,
  reducedFormattedPrice = '0',
  reducedPriceTitle = '0',
}) => {
  const intl = useIntl();
  const [passIsValid] = usePass();
  const [distanceToLocation, setDistanceToLocation] = useState();
  const startTargetRef = useRef(null);

  const { currentUser, currentUserInProgress } = useAppSelector((state) => ({
    currentUser: state.user.currentUser,
    currentUserInProgress: state.user.currentUserInProgress,
  }));
  const { attributes } = currentListing;
  const { geolocation, publicData } = attributes;
  const { locationPlace } = publicData;

  useEffect(() => {
    const getDistance = async () => {
      try {
        if (typeof window !== 'undefined' && !window.currentLocation) {
          // A passive extra: it never opens the location prompt.
          if (!(await hasLocationPermission())) return null;
          window.currentLocation = await userLocation();
        }
        return getDistanceToListing(geolocation);
      } catch (e) {
        return null;
      }
    };
    getDistance().then((distance) => setDistanceToLocation(distance));
  }, [geolocation]);

  const prePriceTag = intl.formatMessage({ id: 'ListingPage.fromPricePreTag' });

  const categoryKey = publicData.category ? publicData.category : REQUEST_BOOKING;
  const categoryOptions = findOptionsForSelectFilter('category', config.custom.filters);
  const categoryLabel = <span>{getCategoryLabel(categoryOptions, categoryKey, intl)}</span>;

  const metadata = currentListing?.attributes?.metadata;
  const listingHasSpecialPrice = metadata?.annualPassPrice;
  const priceAmount =
    !!listingHasSpecialPrice && passIsValid
      ? listingHasSpecialPrice.amount
      : currentListing?.attributes?.price?.amount || 0;
  const isPlusListing = priceAmount >= config.annualPass.lightPriceThreshold;
  const isCampingGround = isCampingGroundListing(currentListing);

  return (
    <Container maxWidth="xl" sx={{ marginTop: 4, marginBottom: 4 }}>
      <Grid container spacing={2}>
        <Grid size={{ lg: 8, xs: 12 }}>
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: {
                  xs: 'center',
                  md: 'flex-start',
                },
                textAlign: {
                  xs: 'center',
                  md: 'left',
                },
                marginBottom: {
                  xs: 2,
                  sm: 0,
                },
              }}
            >
              <Typography component="h1" variant="h4">
                {richTitle}
                {currentUserInProgress || (currentUserInProgress && !currentUser) ? (
                  <Skeleton sx={{ display: 'inline-block', ml: 2 }} variant="text" width={100} />
                ) : !passIsValid ? (
                  <>
                    {` ${prePriceTag} ${formattedPrice} `}
                    <FormattedMessage id="ListingPage.perNight" />
                  </>
                ) : showReducedPrice ? (
                  <>
                    {` ${prePriceTag} ${reducedFormattedPrice} `}
                    <Box
                      component="span"
                      sx={{
                        textDecoration: 'line-through',
                        color: 'text.disabled',
                      }}
                    >{`${formattedPrice}`}</Box>{' '}
                    <FormattedMessage id="ListingPage.perNight" />
                  </>
                ) : null}
              </Typography>
              {/*{isTranslated ? (*/}
              {/*  <Box ml={1}>*/}
              {/*    <Tooltip*/}
              {/*      title={intl.formatMessage({ id: 'ListingPage.description.translation' })}*/}
              {/*    >*/}
              {/*      <GTranslateIcon fontSize="small" />*/}
              {/*    </Tooltip>*/}
              {/*  </Box>*/}
              {/*) : null}*/}
            </Box>

            {/* Location Section */}
            <Box
              alignItems="center"
              display="flex"
              flexDirection={{
                xs: 'column',
                sm: 'row',
              }}
              justifyContent={{
                xs: 'center',
                md: 'flex-start',
              }}
            >
              <Chip
                icon={<IconLocationPin sx={{ color: petrol.main }} />}
                label={
                  <Typography component="span" variant="body2">
                    {locationPlace}
                    {distanceToLocation ? (
                      <Typography component="span" variant="body1">
                        {distanceToLocation ? <span>&nbsp;</span> : null}
                        <FormattedMessage
                          id="ListingPage.distanceToListing"
                          values={{ distance: distanceToLocation }}
                        />
                      </Typography>
                    ) : null}
                  </Typography>
                }
                ml={1}
                sx={{
                  '& .MuiChip-label': {
                    whiteSpace: 'unset',
                    overflow: 'unset',
                    textOverflow: 'unset',
                  },
                  '& .MuiSvgIcon-root': {
                    color: petrol.main,
                  },
                }}
                variant="info"
              />

              <Chip
                icon={<InfoOutlinedIcon />}
                label={
                  <Typography component="span" variant="body2">
                    {categoryLabel}
                  </Typography>
                }
                ml={1}
                sx={{
                  '& .MuiSvgIcon-root': {
                    color: petrol.main,
                  },
                }}
                variant="info"
              />
              {listingHasSpecialPrice ? null : isPlusListing ? (
                <Chip
                  icon={<AddCircleOutlineIcon sx={{ color: petrol.main }} />}
                  label={
                    <Typography component="span" variant="body2">
                      <FormattedMessage id="ListingPage.plusListing" />
                    </Typography>
                  }
                  ml={1}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      color: petrol.main,
                    },
                  }}
                  variant="info"
                />
              ) : null}
              <Chip
                icon={<HomeOutlinedIcon sx={{ color: petrol.main }} />}
                label={
                  <Typography component="span" variant="body2">
                    <FormattedMessage
                      id={
                        isCampingGround
                          ? 'ListingPage.categoryCampingGround'
                          : 'ListingPage.categoryPrivateSpot'
                      }
                    />
                  </Typography>
                }
                ml={1}
                sx={{
                  '& .MuiSvgIcon-root': {
                    color: petrol.main,
                  },
                }}
                variant="info"
              />
            </Box>
            <ListingFunctionsSection
              currentListing={currentListing}
              onShareListing={onShareListing}
              reviewsRef={reviewsRef}
            />
          </Box>
        </Grid>
        <Grid ref={startTargetRef} size={{ lg: 4, xs: 12 }} sx={{ position: 'relative' }}>
          <BookingPanel
            authorDisplayName={authorDisplayName}
            bookingLimit={bookingLimit}
            category={categoryKey}
            currentListing={currentListing}
            customLineItemTranslations={customLineItemTranslations}
            distanceToLocation={distanceToLocation}
            fetchLineItemsError={fetchLineItemsError}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchTimeSlotsError={fetchTimeSlotsError}
            fetchTimeSlotsInProgress={fetchTimeSlotsInProgress}
            formattedPrice={formattedPrice}
            getTranslation={getTranslation}
            isOwnListing={isOwnListing}
            lineItems={lineItems}
            minimumNights={minimumNights}
            onFetchTimeSlots={onFetchTimeSlots}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            onShareListing={onShareListing}
            onSubmit={onSubmit}
            openShareModal={openShareModal}
            priceTitle={passIsValid && showReducedPrice ? reducedPriceTitle : priceTitle}
            scrollTargetRef={scrollTargetRef}
            startTargetRef={startTargetRef}
            timeSlots={timeSlots}
            title={title}
            toManyTransactionsWithProvider={toManyTransactionsWithProvider}
            voucher={voucher}
            voucherValidationError={voucherValidationError}
            voucherValidationInProgress={voucherValidationInProgress}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

SectionHeadingComponent.displayName = 'SectionHeading';

const SectionHeading = memo(SectionHeadingComponent);

export default SectionHeading;
